import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { useAuth } from '../contexts/AuthContext';
import * as appointmentsApi from '../api/appointments';
import * as opportunitiesApi from '../api/opportunities';
import * as clientsApi from '../api/clients';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import Modal from '../components/Modal';
import MessageModal from '../components/MessageModal';
import PageHeader from '../components/PageHeader';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

// Utility functions
const parseLocalDateTime = (dateTimeString) => {
  if (!dateTimeString) return new Date();
  // Handle formats like "2024-01-15T13:00:00" or "2024-01-15 13:00:00"
  const normalized = dateTimeString.replace(' ', 'T');
  const [datePart, timePart] = normalized.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = (timePart || '00:00:00').split(':').map(Number);
  // Create date using local timezone (not UTC)
  return new Date(year, month - 1, day, hours, minutes, seconds || 0);
};

const formatLocalDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

function AppointmentsPage() {
  const { loading: authLoading, user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [showAppointmentRequestsModal, setShowAppointmentRequestsModal] = useState(false);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    if (!authLoading) {
      fetchData();
    }
  }, [authLoading]);

  const fetchData = async () => {
    try {
      setError(null);
      setLoading(true);

      const [appointmentsResponse, opportunitiesResponse, clientsResponse] = await Promise.all([
        appointmentsApi.fetchAppointments(),
        opportunitiesApi.getOpportunities(),
        clientsApi.fetchClients()
      ]);

      // Transform appointments for calendar
      const calendarEvents = (appointmentsResponse.appointments || []).map(appointment => ({
        id: appointment.id,
        title: appointment.title || 'Untitled Appointment',
        start: parseLocalDateTime(appointment.start_datetime),
        end: parseLocalDateTime(appointment.end_datetime),
        resource: appointment
      }));
      console.log('AppointmentsPage: calendarEvents:', calendarEvents);

      setAppointments(calendarEvents);
      setOpportunities(opportunitiesResponse.opportunities || []);
      setClients(clientsResponse.clients || []);
      console.log('AppointmentsPage: appointments state after set:', calendarEvents);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    console.log('Calendar event clicked:', event);
    console.log('Event resource (appointment data):', event.resource);
    console.log('Event resource start_datetime:', event.resource.start_datetime);
    console.log('Event start (parsed date):', event.start);
    setSelectedAppointment(event.resource);
    setShowAppointmentModal(true);
  };

  const handleSelectSlot = ({ start, end }) => {
    setSelectedAppointment({
      start_datetime: start.toISOString(),
      end_datetime: end.toISOString(),
      title: '',
      description: '',
      location: '',
      status: 'scheduled'
    });
    setShowNewAppointmentModal(true);
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#3788d8';
    
    if (event.resource) {
      switch (event.resource.status) {
        case 'confirmed':
          backgroundColor = '#059669';
          break;
        case 'completed':
          backgroundColor = '#10b981';
          break;
        case 'cancelled':
          backgroundColor = '#dc2626';
          break;
        case 'rescheduled':
          backgroundColor = '#f59e0b';
          break;
        default:
          backgroundColor = '#3788d8';
      }
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  if (authLoading || loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Calendar */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
          {/* Header */}
          <PageHeader title="Appointments">
            <button
              onClick={() => setShowAppointmentRequestsModal(true)}
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300"
            >
              Appointment Requests ({opportunities.filter(opp => opp.status === 'appointment_requested').length})
            </button>
            <button
              onClick={() => setShowNewAppointmentModal(true)}
              className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300"
            >
              + New Appointment
            </button>
          </PageHeader>

          {success && (
            <div className="mb-4 mt-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md">
              {success}
            </div>
          )}

          <div className="mt-6 w-full max-w-full">
          <div className="h-[600px] md:h-[700px] overflow-x-auto w-full max-w-full">
            <Calendar
              localizer={localizer}
              events={appointments}
              startAccessor="start"
              endAccessor="end"
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              eventPropGetter={eventStyleGetter}
              views={['month', 'week', 'day', 'agenda']}
              step={30}
              showMultiDayTimes
              className="rbc-calendar"
            />
          </div>
          </div>
        </div>

        {/* Appointment Details Modal */}
        {showAppointmentModal && selectedAppointment && (
          <AppointmentDetailsModal
            appointment={selectedAppointment}
            clients={clients}
            opportunities={opportunities}
            user={user}
            onClose={() => {
              setShowAppointmentModal(false);
              setSelectedAppointment(null);
            }}
            onUpdate={(message) => {
              fetchData();
              setShowAppointmentModal(false);
              setSelectedAppointment(null);
              if (message) {
                setSuccess(message);
                setTimeout(() => setSuccess(null), 3000);
              }
            }}
          />
        )}

        {/* New Appointment Modal */}
        {showNewAppointmentModal && (
          <NewAppointmentModal
            appointment={selectedAppointment}
            opportunities={opportunities}
            clients={clients}
            onClose={() => {
              setShowNewAppointmentModal(false);
              setSelectedAppointment(null);
            }}
            onSuccess={(message) => {
              setShowNewAppointmentModal(false);
              setSelectedAppointment(null);
              setSuccess(message);
              setTimeout(() => setSuccess(null), 3000);
              fetchData();
            }}
          />
        )}

        {/* Appointment Requests Modal */}
        {showAppointmentRequestsModal && (
          <AppointmentRequestsModal
            appointmentRequests={opportunities.filter(opp => opp.status === 'appointment_requested')}
            onClose={() => setShowAppointmentRequestsModal(false)}
            onScheduleAppointment={(opportunity) => {
              setSelectedAppointment({
                opportunityId: opportunity.id,
                title: `Estimate for ${opportunity.client_name} - ${opportunity.project_name || 'Project'}`,
                description: opportunity.service_details || opportunity.project_description || '',
                startDatetime: opportunity.preferred_date ?
                  (opportunity.preferred_date.includes('T') ?
                    `${opportunity.preferred_date.split('T')[0]}T09:00` :
                    `${opportunity.preferred_date}T09:00`) : '',
                location: opportunity.address || '',
                status: 'scheduled'
              });
              setShowAppointmentRequestsModal(false);
              setShowNewAppointmentModal(true);
            }}
            onSuccess={(message) => {
              setSuccess(message);
              setTimeout(() => setSuccess(null), 3000);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
}

// Appointment Details Modal
const AppointmentDetailsModal = ({ appointment, clients, opportunities, user, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: appointment.title || '',
    description: appointment.description || '',
    startDatetime: appointment.start_datetime ?
      // Extract time directly from string without moment.js UTC conversion
      appointment.start_datetime.replace(' ', 'T').substring(0, 16) : '',
    duration: appointment.start_datetime && appointment.end_datetime ?
      Math.round((parseLocalDateTime(appointment.end_datetime) - parseLocalDateTime(appointment.start_datetime)) / (1000 * 60)) : 60,
    location: appointment.location || '',
    status: appointment.status || 'scheduled'
  });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState(null);

  // Find the full client and opportunity objects
  const selectedClient = clients.find(c => c.id === appointment.client_id);
  const selectedOpportunity = opportunities.find(opp => opp.id === appointment.opportunity_id);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) {
      return;
    }

    try {
      setLoading(true);
      await appointmentsApi.deleteAppointment(appointment.id);
      onUpdate('Appointment deleted successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate end time from start time + duration
      const startDate = parseLocalDateTime(formData.startDatetime);
      const endDate = new Date(startDate.getTime() + (formData.duration * 60000)); // Convert minutes to milliseconds
      const newEndDatetime = formatLocalDateTime(endDate);

      // Check if the appointment was rescheduled (date/time changed or status explicitly set to rescheduled)
      // Extract time directly from strings without moment.js UTC conversion
      const originalStart = appointment.start_datetime ? appointment.start_datetime.replace(' ', 'T').substring(0, 16) : '';
      const originalEnd = appointment.end_datetime ? appointment.end_datetime.replace(' ', 'T').substring(0, 16) : '';
      const isRescheduled = (formData.startDatetime !== originalStart || newEndDatetime !== originalEnd || formData.status === 'rescheduled');
      
      await appointmentsApi.updateAppointment(appointment.id, {
        title: formData.title,
        description: formData.description,
        startDatetime: formData.startDatetime,
        endDatetime: newEndDatetime,
        location: formData.location,
        status: formData.status
      });
      
      setIsEditing(false);

      if (isRescheduled) {
          setEmailModalData({
              templateType: 'appointment_rescheduled',
              recipientEmail: selectedClient?.email || '',
              contextData: {
                  client_name: selectedClient?.name || '',
                  appointment_title: formData.title,
                  appointment_date: moment(formData.startDatetime).format('MMMM D, YYYY'),
                  appointment_time: moment(formData.startDatetime).format('h:mm A'),
                  appointment_location: formData.location || '',
                  ...(selectedOpportunity?.project_name && { project_name: selectedOpportunity.project_name }),
                  company_name: user?.companyName || user?.username || ''
              },
              opportunityId: appointment.opportunity_id || null
          });
          setShowEmailModal(true);
      } else {
          onUpdate('Appointment updated successfully!');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form data to original values
    setFormData({
      title: appointment.title || '',
      description: appointment.description || '',
      startDatetime: appointment.start_datetime ?
        // Extract time directly from string without moment.js UTC conversion
        appointment.start_datetime.replace(' ', 'T').substring(0, 16) : '',
      duration: appointment.start_datetime && appointment.end_datetime ?
        Math.round((parseLocalDateTime(appointment.end_datetime) - parseLocalDateTime(appointment.start_datetime)) / (1000 * 60)) : 60,
      location: appointment.location || '',
      status: appointment.status || 'scheduled'
    });
    setIsEditing(false);
    setError(null);
  };

  return (
    <Modal onClose={onClose} title={isEditing ? "Edit Appointment" : "Appointment Details"}>
      <div className="space-y-4">
        {error && <ErrorMessage message={error} />}
        
        {isEditing ? (
          // Edit Mode
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date & Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={formData.startDatetime}
                  onChange={(e) => setFormData({...formData, startDatetime: e.target.value})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Duration *</label>
                <select
                  value={formData.duration}
                  onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                  <option value={150}>2.5 hours</option>
                  <option value={180}>3 hours</option>
                  <option value={240}>4 hours</option>
                </select>
              </div>
            </div>

            {/* Show calculated end time */}
            {formData.startDatetime && (() => {
              const startDate = parseLocalDateTime(formData.startDatetime);
              const endDate = new Date(startDate.getTime() + (formData.duration * 60000));
              return (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                  <p className="text-sm text-gray-600">
                    <strong>Appointment will end at:</strong> {' '}
                    {endDate.toLocaleString()}
                  </p>
                </div>
              );
            })()}
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="scheduled">Scheduled</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        ) : (
          // View Mode
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Client</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedClient ? (
                    <Link
                      to={`/clients/${selectedClient.id}`}
                      className="text-primary-600 hover:text-primary-800 hover:underline"
                    >
                      {selectedClient.name}
                    </Link>
                  ) : (
                    'Unknown Client'
                  )}
                </p>
              </div>
              {/* Appointment Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <p className="mt-1 text-sm text-gray-900">{appointment.title}</p>
              </div>

              {/* Address */}
              {(appointment.location || selectedClient?.address) && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <p className="mt-1 text-sm text-gray-900">
                    <a
                      href={`https://maps.google.com/maps?q=${encodeURIComponent(appointment.location || selectedClient.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      📍 {appointment.location || selectedClient.address}
                    </a>
                  </p>
                </div>
              )}

              {/* Email */}
              {selectedClient?.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">
                    <a
                      href={`mailto:${selectedClient.email}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {selectedClient.email}
                    </a>
                  </p>
                </div>
              )}

              {/* Phone */}
              {selectedClient?.phone && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="mt-1 text-sm text-gray-900">
                    <a
                      href={`tel:${selectedClient.phone}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {selectedClient.phone}
                    </a>
                  </p>
                </div>
              )}
              
              {/* Start Date & Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Start</label>
                <p className="mt-1 text-sm text-gray-900">
                  {(() => {
                    const parsedDate = parseLocalDateTime(appointment.start_datetime);
                    console.log('Appointment modal - raw start_datetime:', appointment.start_datetime);
                    console.log('Appointment modal - parsed date object:', parsedDate);
                    console.log('Appointment modal - formatted:', parsedDate.toLocaleString());
                    return parsedDate.toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    });
                  })()}
                </p>
              </div>

              {/* End Date & Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700">End</label>
                <p className="mt-1 text-sm text-gray-900">
                  {parseLocalDateTime(appointment.end_datetime).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>

              {/* Duration */}
              {appointment.start_datetime && appointment.end_datetime && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {Math.round((new Date(appointment.end_datetime) - new Date(appointment.start_datetime)) / (1000 * 60))} minutes
                  </p>
                </div>
              )}

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`mt-1 inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                  appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  appointment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                  appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  appointment.status === 'rescheduled' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {appointment.status}
                </span>
              </div>
            </div>
            
            {appointment.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <p className="mt-1 text-sm text-gray-900">{appointment.description}</p>
              </div>
            )}
            
            <div className="flex justify-between pt-4">
              <div className="flex space-x-2">
                <button
                  onClick={() => setIsEditing(true)}
                  disabled={loading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg shadow-md hover:bg-primary-700 disabled:opacity-50"
                >
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-md hover:bg-red-700 disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg shadow-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>

      {showEmailModal && emailModalData && (
        <MessageModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            onUpdate('Appointment updated successfully!'); // Call parent onUpdate after email modal closes
          }}
          templateType={emailModalData.templateType}
          recipientEmail={emailModalData.recipientEmail}
          contextData={emailModalData.contextData}
          opportunityId={emailModalData.opportunityId}
          onSuccess={() => {
            setShowEmailModal(false);
            onUpdate('Appointment updated and email sent successfully!');
          }}
        />
      )}
    </Modal>
  );
};

// New Appointment Modal
const NewAppointmentModal = ({ appointment, opportunities, clients, onClose, onSuccess }) => {
  const { user } = useAuth(); // Access user from AuthContext
  const [formData, setFormData] = useState({
    title: appointment?.title || 'Estimate',
    description: appointment?.description || '',
    startDatetime: appointment?.start_datetime || '',
    duration: 60, // Duration in minutes (default 1 hour)
    location: appointment?.location || '',
    status: appointment?.status || 'scheduled',
    opportunityId: appointment?.opportunityId || '',
    clientId: appointment?.clientId || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newlyCreatedAppointment, setNewlyCreatedAppointment] = useState(null);

  useEffect(() => {
    if (appointment && !formData.startDatetime) {
      // Calculate duration from start and end times if both are available
      let duration = 60; // default 1 hour
      if (appointment.start_datetime && appointment.end_datetime) {
        const startTime = parseLocalDateTime(appointment.start_datetime);
        const endTime = parseLocalDateTime(appointment.end_datetime);
        duration = Math.round((endTime - startTime) / (1000 * 60)); // Convert to minutes
      }

      setFormData(prev => ({
        ...prev,
        startDatetime: appointment.start_datetime ?
          // Extract time directly from string without moment.js UTC conversion
          appointment.start_datetime.replace(' ', 'T').substring(0, 16) : '',
        duration: duration || 60
      }));
    }
  }, [appointment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Calculate end time from start time + duration
      const startDate = parseLocalDateTime(formData.startDatetime);
      const endDate = new Date(startDate.getTime() + (formData.duration * 60000)); // Convert minutes to milliseconds

      const appointmentData = {
        title: formData.title,
        description: formData.description,
        startDatetime: formData.startDatetime,
        endDatetime: formatLocalDateTime(endDate),
        location: formData.location,
        status: formData.status,
        opportunityId: formData.opportunityId || null,
        clientId: formData.clientId || null
      };

      const response = await appointmentsApi.createAppointment(appointmentData);
      setNewlyCreatedAppointment(response.appointment); // Store the newly created appointment

      // Update opportunity status to appointment_scheduled if an opportunity was selected
      if (formData.opportunityId) {
        try {
          await opportunitiesApi.updateOpportunityStatus(formData.opportunityId, 'appointment_scheduled');
        } catch (statusError) {
          console.error('Warning: Failed to update opportunity status:', statusError);
          // Don't fail the entire appointment creation if status update fails
        }
      }

      // Instead of calling onSuccess directly, open the email modal
      setShowEmailModal(true);
      console.log('NewAppointmentModal: setShowEmailModal(true) called.');

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

      const selectedClient = clients.find(c => c.id === parseInt(formData.clientId));

      const selectedOpportunity = opportunities.find(opp => opp.id === parseInt(formData.opportunityId));

    

      return (

        <Modal onClose={onClose} title="New Appointment">

        <form onSubmit={handleSubmit} className="space-y-4">

          {error && <ErrorMessage message={error} />}

          <div>

            <label className="block text-sm font-medium text-gray-700">Client (Optional)</label>

            <select

              value={formData.clientId}

              onChange={(e) => {
                const clientId = e.target.value;
                const selectedClient = clients.find(c => c.id === parseInt(clientId));
                setFormData({
                  ...formData,
                  clientId: clientId,
                  location: selectedClient?.address || formData.location
                });
              }}

              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

            >

              <option value="">Select a client (optional)</option>

              {clients.map(client => (

                <option key={client.id} value={client.id}>

                  {client.name} {client.email ? `(${client.email})` : ''}

                </option>

              ))}

            </select>

            <p className="mt-1 text-sm text-gray-500">Select a client for general appointments not tied to a specific project</p>

          </div>

          <div>

            <label className="block text-sm font-medium text-gray-700">Title *</label>

            <input

              type="text"

              required

              value={formData.title}

              onChange={(e) => setFormData({...formData, title: e.target.value})}

              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

            />

          </div>

          <div>

            <label className="block text-sm font-medium text-gray-700">Description</label>

            <textarea

              rows="3"

              value={formData.description}

              onChange={(e) => setFormData({...formData, description: e.target.value})}

              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

            />

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>

              <label className="block text-sm font-medium text-gray-700">Start Date & Time *</label>

              <input

                type="datetime-local"

                required

                value={formData.startDatetime}

                onChange={(e) => setFormData({...formData, startDatetime: e.target.value})}

                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

              />

            </div>



            <div>

              <label className="block text-sm font-medium text-gray-700">Duration *</label>

              <select

                value={formData.duration}

                onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}

                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

              >

                <option value={15}>15 minutes</option>

                <option value={30}>30 minutes</option>

                <option value={45}>45 minutes</option>

                <option value={60}>1 hour</option>

                <option value={90}>1.5 hours</option>

                <option value={120}>2 hours</option>

                <option value={150}>2.5 hours</option>

                <option value={180}>3 hours</option>

                <option value={240}>4 hours</option>

              </select>

            </div>

          </div>



          {/* Show calculated end time */}

          {formData.startDatetime && (() => {
            const startDate = parseLocalDateTime(formData.startDatetime);
            const endDate = new Date(startDate.getTime() + (formData.duration * 60000));
            return (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <p className="text-sm text-gray-600">
                  <strong>Appointment will end at:</strong> {' '}
                  {endDate.toLocaleString()}
                </p>
              </div>
            );
          })()}

          <div>

            <label className="block text-sm font-medium text-gray-700">Location</label>

            <input

              type="text"

              value={formData.location}

              onChange={(e) => setFormData({...formData, location: e.target.value})}

              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

            />

          </div>

          <div>

            <label className="block text-sm font-medium text-gray-700">Status</label>

            <select

              value={formData.status}

              onChange={(e) => setFormData({...formData, status: e.target.value})}

              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

            >

              <option value="scheduled">Scheduled</option>

              <option value="confirmed">Confirmed</option>

              <option value="completed">Completed</option>

              <option value="cancelled">Cancelled</option>

              <option value="rescheduled">Rescheduled</option>

            </select>

          </div>



          <div className="flex justify-between pt-4">

            <button

              type="button"

              onClick={onClose}

              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"

            >

              Cancel

            </button>

            <button

              type="submit"

              disabled={loading}

              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"

            >

              {loading ? 'Creating...' : 'Create Appointment'}

            </button>

          </div>

        </form>

  

        {showEmailModal && newlyCreatedAppointment && selectedClient && (

          <MessageModal

            isOpen={showEmailModal}

            onClose={() => {

              setShowEmailModal(false);

              onSuccess('Appointment created successfully!'); // Call parent onSuccess after email modal closes

            }}

            templateType="appointment_scheduled"

            recipientEmail={selectedClient.email || ''}

                      contextData={{

                        client_name: selectedClient.name || '',

                        appointment_title: newlyCreatedAppointment.title || '',

                        appointment_date: moment(newlyCreatedAppointment.start_datetime).format('MMMM D, YYYY'),

                        appointment_time: moment(newlyCreatedAppointment.start_datetime).format('h:mm A'),

                        appointment_location: newlyCreatedAppointment.location || '',

                        ...(selectedOpportunity?.project_name && { project_name: selectedOpportunity.project_name }),

                        company_name: user?.companyName || user?.username || ''

                      }}

            opportunityId={newlyCreatedAppointment.opportunityId || null}

            onSuccess={() => {

              setShowEmailModal(false);

              onSuccess('Appointment created and email sent successfully!');

            }}

          />

        )}

      </Modal>

    );

  };

// Appointment Requests Modal
const AppointmentRequestsModal = ({ appointmentRequests, onClose, onScheduleAppointment }) => {
  const [selectedRequest, setSelectedRequest] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      if (dateString.includes('T') && dateString.includes('Z')) {
        const dateOnly = dateString.split('T')[0];
        return new Date(dateOnly + 'T00:00:00').toLocaleDateString();
      } else {
        return new Date(dateString + 'T00:00:00').toLocaleDateString();
      }
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Show schedule modal if a request is selected
  if (selectedRequest) {
    return (
      <ScheduleAppointmentModalForRequests
        request={selectedRequest}
        onClose={() => {
          setSelectedRequest(null);
        }}
        onSuccess={() => {
          setSelectedRequest(null);
          onClose();
        }}
        formatDate={formatDate}
      />
    );
  }

  return (
    <Modal onClose={onClose} title="Appointment Requests">
      <div className="max-h-[600px] overflow-y-auto">
        {appointmentRequests.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">📅</div>
            <p className="text-gray-500">No appointment requests at this time</p>
          </div>
        ) : (
          <div className="space-y-4">
            {appointmentRequests.map((request) => (
              <div key={request.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900">
                        {request.client_name}
                      </h4>
                      {request.project_name && (
                        <span className="text-gray-600">- {request.project_name}</span>
                      )}
                      {request.client_type === 'customer' && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                          ⭐ Repeat Customer
                        </span>
                      )}
                    </div>

                    {request.company && (
                      <p className="text-sm text-gray-600 mb-1">{request.company}</p>
                    )}

                    {request.preferred_date && (
                      <div className="text-sm text-blue-600 mb-1">
                        <strong>Preferred Date:</strong> {formatDate(request.preferred_date)}
                        {request.preferred_time_range && ` at ${request.preferred_time_range}`}
                      </div>
                    )}

                    {(request.service_details || request.project_description) && (
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Details:</strong> {request.service_details || request.project_description}
                      </p>
                    )}

                    {request.estimated_value && (
                      <p className="text-sm text-gray-700 font-medium mb-1">
                        <strong>Estimated Value:</strong> ${parseFloat(request.estimated_value).toFixed(2)}
                      </p>
                    )}

                    <p className="text-xs text-gray-400 mt-2">
                      Created: {formatDate(request.created_at)}
                    </p>
                  </div>

                  <div className="ml-4 flex flex-col gap-2">
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg shadow-sm hover:bg-blue-700 transition duration-200"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4 mt-4 border-t border-gray-200">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

// Schedule Appointment Modal for Appointment Requests
const ScheduleAppointmentModalForRequests = ({ request, onClose, onSuccess, formatDate }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: `Estimate for ${request.client_name} - ${request.project_name || 'Project'}`,
    description: request.service_details || request.project_description || '',
    startDatetime: request.preferred_date ?
      (request.preferred_date.includes('T') ?
        `${request.preferred_date.split('T')[0]}T09:00` :
        `${request.preferred_date}T09:00`) : '',
    duration: 60,
    location: request.client_address || '',
    status: 'scheduled'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newlyCreatedAppointment, setNewlyCreatedAppointment] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      const { createAppointment } = await import('../api/appointments');

      const startDate = parseLocalDateTime(formData.startDatetime);
      const endDate = new Date(startDate.getTime() + (formData.duration * 60000));

      const appointmentData = {
        title: formData.title,
        description: formData.description,
        startDatetime: formData.startDatetime,
        endDatetime: formatLocalDateTime(endDate),
        location: formData.location,
        status: formData.status,
        opportunityId: request.id
      };

      const response = await createAppointment(appointmentData);
      setNewlyCreatedAppointment(response.appointment);

      // Update opportunity status to appointment_scheduled
      try {
        await opportunitiesApi.updateOpportunityStatus(request.id, 'appointment_scheduled');
      } catch (statusError) {
        console.error('Warning: Failed to update opportunity status:', statusError);
      }

      setShowEmailModal(true);
    } catch (err) {
      setError(err.message || 'Failed to schedule appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Schedule Appointment">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorMessage message={error} />}

        {/* Display appointment request details */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-25 border border-blue-300 rounded-lg p-5 mb-6 shadow-sm">
          <h3 className="font-semibold text-blue-900 mb-4 text-base flex items-center">
            <span className="text-lg mr-2">👤</span>Client Information
          </h3>

          {/* Client Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Client Name */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Client Name</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">{request.client_name || 'Not provided'}</p>
            </div>

            {/* Phone Number */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Phone Number</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">
                {request.client_phone ? (
                  <a href={`tel:${request.client_phone}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                    {request.client_phone}
                  </a>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>

            {/* Email Address */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Email Address</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">
                {request.client_email ? (
                  <a href={`mailto:${request.client_email}`} className="text-blue-600 hover:text-blue-800 hover:underline break-all">
                    {request.client_email}
                  </a>
                ) : (
                  'Not provided'
                )}
              </p>
            </div>

            {/* Address */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Address</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">{request.client_address || 'Not provided'}</p>
            </div>

            {/* Company */}
            {request.company && (
              <div className="bg-white rounded-md p-3 border border-blue-100">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Company</p>
                <p className="text-sm font-semibold text-gray-800 mt-1">{request.company}</p>
              </div>
            )}

            {/* Project Name */}
            <div className="bg-white rounded-md p-3 border border-blue-100">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Project/Service</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">{request.project_name || 'Not specified'}</p>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-blue-200 my-4"></div>

          {/* Appointment Preferences */}
          <h4 className="font-semibold text-blue-900 mb-3 text-sm flex items-center">
            <span className="text-lg mr-2">📅</span>Preferred Appointment Details
          </h4>

          <div className="space-y-3">
            {/* Preferred Date */}
            {request.preferred_date ? (
              <div className="flex items-start space-x-3 bg-blue-100 rounded-md p-3">
                <span className="text-lg">📅</span>
                <div>
                  <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Preferred Date</p>
                  <p className="text-base font-bold text-blue-900">{formatDate(request.preferred_date)}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start space-x-3 bg-gray-100 rounded-md p-3 opacity-60">
                <span className="text-lg">📅</span>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Preferred Date</p>
                  <p className="text-sm text-gray-600">Not specified</p>
                </div>
              </div>
            )}

            {/* Preferred Time Range */}
            {request.preferred_time_range ? (
              <div className="flex items-start space-x-3 bg-blue-100 rounded-md p-3">
                <span className="text-lg">⏰</span>
                <div>
                  <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Preferred Time</p>
                  <p className="text-base font-bold text-blue-900">{request.preferred_time_range}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start space-x-3 bg-gray-100 rounded-md p-3 opacity-60">
                <span className="text-lg">⏰</span>
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Preferred Time</p>
                  <p className="text-sm text-gray-600">Not specified</p>
                </div>
              </div>
            )}

            {/* Service Details */}
            {request.service_details && (
              <div className="flex items-start space-x-3 bg-amber-50 rounded-md p-3 border border-amber-200">
                <span className="text-lg">📝</span>
                <div>
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">Service Details</p>
                  <p className="text-sm text-amber-900 mt-1">{request.service_details}</p>
                </div>
              </div>
            )}

            {/* Project Description */}
            {request.project_description && !request.service_details && (
              <div className="flex items-start space-x-3 bg-amber-50 rounded-md p-3 border border-amber-200">
                <span className="text-lg">📝</span>
                <div>
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">Project Description</p>
                  <p className="text-sm text-amber-900 mt-1">{request.project_description}</p>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {request.notes && (
              <div className="flex items-start space-x-3 bg-purple-50 rounded-md p-3 border border-purple-200">
                <span className="text-lg">💬</span>
                <div>
                  <p className="text-xs font-medium text-purple-700 uppercase tracking-wide">Additional Notes</p>
                  <p className="text-sm text-purple-900 mt-1">{request.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Appointment Title *</label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date & Time *</label>
            <input
              type="datetime-local"
              required
              value={formData.startDatetime}
              onChange={(e) => setFormData({...formData, startDatetime: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Duration *</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={150}>2.5 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>
        </div>

        {/* Show calculated end time */}
        {formData.startDatetime && (() => {
          const startDate = parseLocalDateTime(formData.startDatetime);
          const endDate = new Date(startDate.getTime() + (formData.duration * 60000));
          return (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="text-sm text-gray-600">
                <strong>Appointment will end at:</strong> {' '}
                {endDate.toLocaleString()}
              </p>
            </div>
          );
        })()}

        <div>
          <label className="block text-sm font-medium text-gray-700">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description/Notes</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows="3"
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Scheduling...' : 'Schedule Appointment'}
          </button>
        </div>
      </form>

      {showEmailModal && newlyCreatedAppointment && request.client_email && (
        <MessageModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            onSuccess();
          }}
          templateType="appointment_scheduled"
          recipientEmail={request.client_email || ''}
          contextData={{
            client_name: request.client_name || '',
            appointment_title: newlyCreatedAppointment.title || '',
            appointment_date: moment(newlyCreatedAppointment.start_datetime).format('MMMM D, YYYY'),
            appointment_time: moment(newlyCreatedAppointment.start_datetime).format('h:mm A'),
            appointment_location: newlyCreatedAppointment.location || '',
            project_name: request.project_name || '',
            company_name: user?.companyName || user?.username || ''
          }}
          opportunityId={newlyCreatedAppointment.opportunity_id || null}
          onSuccess={() => {
            setShowEmailModal(false);
            onSuccess();
          }}
        />
      )}
    </Modal>
  );
};

export default AppointmentsPage;